import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth, getOwnerKey } from '../hooks/useAuth';
import { fetchProducts } from '../services/api';
import { productImage, onImgError } from '../utils/images';
import { Product } from '../types';

const NewListPage: React.FC = () => {
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const { user, authHeaders } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [savedProducts, setSavedProducts] = useState<Product[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (favorites.length === 0) { setLoading(false); return; }
      const all = await fetchProducts({ limit: 1000 });
      setSavedProducts(all.filter((p) => favorites.includes(String(p.id))));
      setLoading(false);
    })();
  }, [favorites]);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('Give your list a title.'); return; }
    if (picked.size === 0) { setError('Pick at least one product.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch('/api/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          coverImage: coverImage.trim(),
          items: Array.from(picked).map(Number),
          ownerKey: getOwnerKey(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not create the list');
      navigate(`/lists/${data.token}`);
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <SEO title="Create a list" description="Bundle your saved picks into a shareable GoodCart list." url="https://goodcart.org/lists/new" />
      <div className="container-custom py-10 max-w-3xl">
        <h1 className="font-display text-3xl text-earth-900 mb-2">Create a list</h1>
        <p className="text-earth-600 mb-8">
          Bundle products into a shareable list.
          {!user && (
            <> You can do this as a guest, or <Link to="/account" className="text-sage-700 underline">sign in</Link> to keep lists on your account.</>
          )}
        </p>

        <form onSubmit={create} className="space-y-5 mb-10">
          <div>
            <label htmlFor="list-title" className="block text-sm font-medium text-earth-800 mb-1">Title *</label>
            <input id="list-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
              placeholder="Sunday reset essentials"
              className="w-full py-2.5 px-4 bg-white border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
          </div>
          <div>
            <label htmlFor="list-desc" className="block text-sm font-medium text-earth-800 mb-1">Description</label>
            <textarea id="list-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500}
              placeholder="What's this list for?"
              className="w-full py-2.5 px-4 bg-white border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
          </div>
          <div>
            <label htmlFor="list-cover" className="block text-sm font-medium text-earth-800 mb-1">Cover image URL (optional)</label>
            <input id="list-cover" value={coverImage} onChange={(e) => setCoverImage(e.target.value)} type="url"
              placeholder="https://..."
              className="w-full py-2.5 px-4 bg-white border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
          </div>

          <div>
            <p className="block text-sm font-medium text-earth-800 mb-2">Products from your saved picks *</p>
            {loading ? (
              <div className="flex justify-center py-8"><LoadingSpinner /></div>
            ) : savedProducts.length === 0 ? (
              <p className="text-earth-600 bg-white border border-earth-100 rounded-xl p-5">
                Nothing saved yet. <Link to="/search" className="text-sage-700 underline">Browse products</Link> and tap the heart to add them here.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
                {savedProducts.map((p) => (
                  <label key={p.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    picked.has(String(p.id)) ? 'border-sage-500 bg-sage-50' : 'border-earth-100 bg-white hover:border-earth-200'
                  }`}>
                    <input type="checkbox" checked={picked.has(String(p.id))} onChange={() => toggle(String(p.id))}
                      className="h-4 w-4 accent-sage-600" />
                    <img src={productImage(p)} alt="" className="h-10 w-10 rounded-md object-contain bg-white" onError={onImgError()} />
                    <span className="text-sm text-earth-800 line-clamp-2">{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-clay-600 text-sm" role="alert">{error}</p>}

          <button type="submit" disabled={submitting} className="btn btn-primary">
            {submitting ? 'Creating…' : `Create list with ${picked.size} ${picked.size === 1 ? 'item' : 'items'}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewListPage;
